/**
 * Builds the `to` argument for react-router `navigate(...)` when filter components
 * redirect with optional layout segment (User Story #2383).
 * Pure — safe for SSR imports; callers invoke from client event handlers only.
 */

import { isValidLayoutName } from "./basefile.utils";
import { ACTIVE_TOGGLES_QUERY_KEY, parseActiveTogglesParam } from "./active-toggles.utils";

/** First URL path segment for a named layout must start with `~` (see server `extractLayoutNameAndPath`). */
export function normalizeRedirectLayoutSegment(layoutTrimmed: string): string {
  const trimmed = layoutTrimmed.trim();
  const stripped = trimmed.replace(/^\/+|\/+$/g, "");
  if (!stripped) {
    return "";
  }
  if (stripped.startsWith("~")) {
    const rest = stripped.slice(1);
    if (rest && isValidLayoutName(rest)) {
      return `~${rest}`;
    }
    return stripped;
  }
  if (isValidLayoutName(stripped)) {
    return `~${stripped}`;
  }
  return stripped;
}

/** True when either value is non-empty after trim (matches `buildFilterRedirectNavigateTo` empty check). */
export function hasFilterRedirectNavigateIntent(
  redirect?: string,
  redirectLayout?: string,
): boolean {
  return !!((redirect ?? "").trim() || (redirectLayout ?? "").trim());
}

/**
 * Path after `effectiveSitePath` with an optional leading `~layout` segment stripped.
 * Aligns with {@link deriveFilePathFromSiteLocation} layout handling (without default-file fallbacks).
 */
export function getDocumentPathAfterSiteAndOptionalLayout(
  pathname: string,
  effectiveSitePath: string,
): string {
  const trimmedSite = (effectiveSitePath ?? "").replace(/\/+$/, "");
  let remainder = pathname;
  if (trimmedSite && remainder.startsWith(trimmedSite)) {
    remainder = remainder.slice(trimmedSite.length);
  }
  if (!remainder.startsWith("/")) {
    remainder = `/${remainder}`;
  }
  const segments = remainder.split("/").filter(Boolean);
  if (segments.length > 0 && segments[0].startsWith("~")) {
    const afterLayout = segments.slice(1).join("/");
    return afterLayout ? `/${afterLayout}` : "/";
  }
  const doc = segments.join("/");
  return doc ? `/${doc}` : "/";
}

export interface BuildFilterRedirectNavigateToParams {
  effectiveSitePath: string;
  redirect?: string;
  redirectLayout?: string;
  /**
   * `useLocation().pathname`. When `redirectLayout` is set and `redirect` is empty, the built URL
   * keeps this document path (after site and current optional layout). If omitted, layout-only redirects use `/`.
   */
  currentPathname?: string;
  /** `URLSearchParams.prototype.toString()` or empty string */
  searchParamsString: string;
  /** `useLocation().hash` (includes leading `#` when non-empty) */
  locationHash?: string;
  /** Optional one-shot SSR signal used by ToggleSwitchController */
  activeToggles?: string;
}

/**
 * @returns `null` when both `redirect` and `redirectLayout` are empty after trim (no navigation).
 */
export function buildFilterRedirectNavigateTo(
  params: BuildFilterRedirectNavigateToParams,
): string | null {
  const redirectTrimmed = (params.redirect ?? "").trim();
  const layoutTrimmed = (params.redirectLayout ?? "").trim();
  const layoutNorm = layoutTrimmed ? normalizeRedirectLayoutSegment(layoutTrimmed) : "";

  if (!redirectTrimmed && !layoutTrimmed) {
    return null;
  }
  if (!redirectTrimmed && !layoutNorm) {
    return null;
  }

  let pathname: string;
  if (!layoutNorm) {
    pathname = `${params.effectiveSitePath}${redirectTrimmed}`;
  } else {
    let targetPath: string;
    if (redirectTrimmed) {
      targetPath = redirectTrimmed.startsWith("/")
        ? redirectTrimmed
        : `/${redirectTrimmed}`;
    } else if (params.currentPathname != null) {
      targetPath = getDocumentPathAfterSiteAndOptionalLayout(
        params.currentPathname,
        params.effectiveSitePath,
      );
    } else {
      targetPath = "/";
    }
    pathname = joinSitePathLayoutAndTarget(
      params.effectiveSitePath,
      layoutNorm,
      targetPath,
    );
  }

  const searchParams = new URLSearchParams(params.searchParamsString);
  const parsedActiveToggles = parseActiveTogglesParam(params.activeToggles);
  if (parsedActiveToggles.length > 0) {
    searchParams.set(ACTIVE_TOGGLES_QUERY_KEY, parsedActiveToggles.join(","));
  }
  return joinPathSearchAndHash(pathname, searchParams.toString(), params.locationHash);
}

/**
 * Compose a React Router `to` string: pathname + optional `?query` + current hash.
 * `searchParamsString` is `URLSearchParams.prototype.toString()` (no leading `?`).
 * `locationHash` is `useLocation().hash` (`#…` when non-empty, otherwise `""`).
 */
export function joinPathSearchAndHash(
  pathname: string,
  searchParamsString: string,
  locationHash?: string,
): string {
  const queryPart = searchParamsString ? `?${searchParamsString}` : "";
  const hashPart = locationHash ?? "";
  return `${pathname}${queryPart}${hashPart}`;
}

function joinSitePathLayoutAndTarget(
  effectiveSitePath: string,
  layoutSegmentNormalized: string,
  targetPath: string,
): string {
  const base = effectiveSitePath.replace(/\/+$/, "");
  const layoutSegment = layoutSegmentNormalized.replace(/^\/+|\/+$/g, "");
  const path = targetPath.startsWith("/") ? targetPath : `/${targetPath}`;

  if (!base) {
    return `/${layoutSegment}${path}`;
  }

  return `${base}/${layoutSegment}${path}`;
}
